<template>
  <div class="organization-sso mt-5">


    <div v-if="editing" class="d-flex justify-end">
      <audit-trail-button entity-type="IdentityProvider" :entity-id="$route.params.idpId || null" :organization-id="$route.params.idpId ? null : $route.params.id" />
    </div>

    <!-- Provider list -->
    <div v-if="!editing">
      <v-row>
        <v-col class="text-right">
          <v-btn small color="primary" @click="createNew">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('sso.add-provider') }}
          </v-btn>
          <audit-trail-button class="ml-2" entity-type="IdentityProvider" :entity-id="$route.params.idpId || null" :organization-id="$route.params.idpId ? null : $route.params.id" />
        </v-col>
      </v-row>

      <v-row v-if="!loading && providers.length === 0">
        <v-col>
          <v-alert type="info" text>
            {{ $t('sso.no-providers') }}
          </v-alert>
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <v-data-table
              v-if="providers.length > 0"
              :headers="headers"
              :items="providers"
              :loading="loading"
              hide-default-footer
              @click:row="goToProvider"
          >
            <template #item.enabled="{value}">
              <v-icon small :color="value ? 'success' : 'grey'">
                {{ value ? 'mdi-check-circle' : 'mdi-close-circle' }}
              </v-icon>
            </template>

            <template #item.protocol="{value}">
              <v-chip x-small>{{ value.toUpperCase() }}</v-chip>
            </template>

            <template #item.actions="{item}">
              <v-btn icon small @click.stop="deleteProvider(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </div>

    <!-- Provider form -->
    <div v-if="editing">
      <v-row>
        <v-col>
          <v-btn text small @click="cancelEdit">
            <v-icon left small>mdi-arrow-left</v-icon>
            {{ $t('back') }}
          </v-btn>
        </v-col>
      </v-row>

      <!-- URLs card -->
      <v-card v-if="!isNew && providerUrls" outlined class="mt-4 mb-4">
        <v-card-title><h3>URLs</h3></v-card-title>
        <v-card-text>
          <v-simple-table dense>
            <tbody>
              <tr v-for="urlRow in urlRows" :key="urlRow.label">
                <td class="font-weight-medium" style="width: 280px">{{ urlRow.label }}</td>
                <td>
                  <code>{{ urlRow.value }}</code>
                  <v-btn icon x-small class="ml-2" @click="copyToClipboard(urlRow.value)">
                    <v-icon x-small>mdi-content-copy</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>

      <v-form ref="form" lazy-validation>
        <v-card class="mt-4">
          <v-card-title>
            <h3>{{ isNew ? $t('sso.add-provider') : $t('sso.edit-provider') }}</h3>
          </v-card-title>

          <v-card-text>
            <v-tabs v-model="formTab">
              <v-tab>{{ $t('sso.general') }}</v-tab>
              <v-tab>{{ isOidc ? $t('sso.endpoints') : isSaml ? 'SAML' : $t('sso.introspection') }}</v-tab>
              <v-tab>{{ $t('sso.provisioning') }}</v-tab>
              <v-tab>{{ $t('sso.mappings') }}</v-tab>
              <v-tab v-if="!isNew">{{ $t('sso.history') }}</v-tab>
            </v-tabs>

            <v-tabs-items v-model="formTab" class="pt-6">

              <!-- General tab -->
              <v-tab-item>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                        v-if="isNew"
                        v-model="form.protocol"
                        :label="$t('sso.protocol')"
                        :items="protocolOptions"
                        :rules="[commonRules.required]"
                    />
                    <v-text-field
                        v-else
                        :value="protocolLabel"
                        :label="$t('sso.protocol')"
                        readonly
                        disabled
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                        v-model="form.name"
                        :label="$t('sso.name')"
                        :rules="[commonRules.required]"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                        v-model="form.slug"
                        :label="$t('sso.slug')"
                        :hint="$t('sso.slug-hint')"
                        persistent-hint
                        :rules="[commonRules.required]"
                        :disabled="!isNew"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                        v-model="form.allowedTargets"
                        :label="$t('sso.allowed-targets')"
                        :items="targetOptions"
                        multiple
                        chips
                        small-chips
                        hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-switch
                        v-model="form.enabled"
                        :label="$t('sso.enabled')"
                        hide-details
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="4">
                    <v-select
                        v-model="form.accountLinkPolicy"
                        :label="$t('sso.account-link-policy')"
                        :items="accountLinkPolicyOptions"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                        v-model="form.syncMode"
                        :label="$t('sso.sync-mode')"
                        :items="syncModeOptions"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                        v-model.number="form.clockSkewSeconds"
                        :label="$t('sso.clock-skew')"
                        type="number"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" md="6">
                    <v-switch
                        v-model="form.requireVerifiedEmail"
                        :label="$t('sso.require-verified-email')"
                        hide-details
                    />
                  </v-col>
                </v-row>
              </v-tab-item>

              <!-- Protocol-specific tab (OIDC or SAML) -->
              <v-tab-item>

                <!-- OIDC -->
                <div v-if="isOidc">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                          v-model="form.oidc.issuer"
                          :label="$t('sso.issuer')"
                          :hint="$t('sso.issuer-hint')"
                          persistent-hint
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.oidc.clientId"
                          :label="$t('sso.client-id')"
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.oidc.clientSecret"
                          :label="$t('sso.client-secret')"
                          :hint="isNew ? $t('sso.client-secret-hint') : $t('sso.client-secret-placeholder')"
                          persistent-hint
                          :rules="isNew ? [commonRules.required] : []"
                          type="password"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-combobox
                          v-model="form.oidc.scopes"
                          :label="$t('sso.scopes')"
                          multiple
                          chips
                          small-chips
                          deletable-chips
                          hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-switch
                          v-model="form.oidc.discoveryEnabled"
                          :label="$t('sso.discovery-enabled')"
                          :hint="$t('sso.discovery-enabled-hint')"
                          persistent-hint
                      />
                    </v-col>
                  </v-row>

                  <div v-if="!form.oidc.discoveryEnabled" class="mt-4">
                    <v-alert type="warning" text class="mb-4">{{ $t('sso.manual-endpoints-hint') }}</v-alert>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.oidc.authorizationEndpoint" :label="$t('sso.authorization-endpoint')" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.oidc.tokenEndpoint" :label="$t('sso.token-endpoint')" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.oidc.jwksUri" :label="$t('sso.jwks-uri')" />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.oidc.endSessionEndpoint" :label="$t('sso.end-session-endpoint')" />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field v-model="form.oidc.userinfoEndpoint" :label="$t('sso.userinfo-endpoint')" />
                      </v-col>
                    </v-row>
                  </div>
                </div>

                <!-- SAML -->
                <div v-if="isSaml">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.saml.idpEntityId"
                          :label="$t('sso.idp-entity-id')"
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.saml.idpSsoUrl"
                          :label="$t('sso.idp-sso-url')"
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.saml.idpSloUrl"
                          :label="$t('sso.idp-slo-url')"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.saml.idpMetadataUrl"
                          :label="$t('sso.idp-metadata-url')"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-textarea
                          v-model="form.saml.idpCertificate"
                          :label="$t('sso.idp-certificate')"
                          :rules="isNew ? [commonRules.required] : []"
                          rows="4"
                          auto-grow
                          monospace
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                          v-model="form.saml.nameIdFormat"
                          :label="$t('sso.name-id-format')"
                          :items="nameIdFormatOptions"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="4">
                      <v-switch v-model="form.saml.signRequests" :label="$t('sso.sign-requests')" hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-switch v-model="form.saml.wantAssertionsSigned" :label="$t('sso.want-assertions-signed')" hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-switch v-model="form.saml.wantResponseSigned" :label="$t('sso.want-response-signed')" hide-details />
                    </v-col>
                  </v-row>

                  <div v-if="form.saml.signRequests" class="mt-6">
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-textarea v-model="form.saml.spCertificate" :label="$t('sso.sp-certificate')" rows="3" auto-grow monospace />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-textarea v-model="form.saml.spPrivateKey" :label="$t('sso.sp-private-key')" rows="3" auto-grow monospace />
                      </v-col>
                    </v-row>
                  </div>
                </div>

                <!-- Token Introspection -->
                <div v-if="isTokenIntrospection">
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                          v-model="form.tokenIntrospection.introspectionEndpoint"
                          :label="$t('sso.introspection-endpoint')"
                          :placeholder="$t('sso.introspection-endpoint-hint')"
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.tokenIntrospection.clientId"
                          :label="$t('sso.client-id')"
                          :rules="[commonRules.required]"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                          v-model="form.tokenIntrospection.clientSecret"
                          :label="$t('sso.client-secret')"
                          :hint="isNew ? '' : $t('sso.client-secret-placeholder')"
                          persistent-hint
                          :rules="isNew ? [commonRules.required] : []"
                          type="password"
                      />
                    </v-col>
                  </v-row>
                </div>
              </v-tab-item>

              <!-- Provisioning tab -->
              <v-tab-item>
                <v-card v-if="hasTarget('portal')" outlined class="pa-4 mb-4">
                  <h4 class="mb-4">{{ $t('sso.portal-provisioning') }}</h4>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-switch v-model="form.provisioningPolicy.portal.enabled" :label="$t('sso.provisioning-enabled')" hide-details />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                          v-model="form.provisioningPolicy.portal.defaultRole"
                          :label="$t('sso.default-role')"
                          :items="roleOptions"
                          :disabled="!form.provisioningPolicy.portal.enabled"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                          v-model="form.provisioningPolicy.portal.extraOrganizationIds"
                          :label="$t('sso.extra-organizations')"
                          :items="organizationItems"
                          item-text="name"
                          item-value="id"
                          multiple chips small-chips deletable-chips clearable
                          :search-input.sync="organizationSearch"
                          :loading="loadingOrganizations"
                          :disabled="!form.provisioningPolicy.portal.enabled"
                          hide-no-data
                      />
                    </v-col>
                  </v-row>
                </v-card>

                <v-card v-if="hasTarget('webapp')" outlined class="pa-4">
                  <h4 class="mb-4">{{ $t('sso.webapp-provisioning') }}</h4>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-switch v-model="form.provisioningPolicy.webapp.enabled" :label="$t('sso.provisioning-enabled')" hide-details />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-switch
                          v-model="form.provisioningPolicy.webapp.autoCreateMember"
                          :label="$t('sso.auto-create-member')"
                          :hint="$t('sso.auto-create-member-hint')"
                          persistent-hint
                          :disabled="!form.provisioningPolicy.webapp.enabled"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-select
                          v-model="form.provisioningPolicy.webapp.defaultMemberTypeId"
                          :label="$t('sso.default-member-type')"
                          :items="memberTypeItems" item-text="name" item-value="id"
                          clearable :disabled="!form.provisioningPolicy.webapp.enabled" :loading="loadingMemberTypes"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-select
                          v-model="form.provisioningPolicy.webapp.defaultMemberGroupIds"
                          :label="$t('sso.default-member-groups')"
                          :items="memberGroupItems" item-text="name" item-value="id"
                          multiple chips small-chips deletable-chips clearable
                          :disabled="!form.provisioningPolicy.webapp.enabled" :loading="loadingMemberGroups"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-autocomplete
                          v-model="form.provisioningPolicy.webapp.extraOrganizationIds"
                          :label="$t('sso.extra-organizations')"
                          :items="organizationItems" item-text="name" item-value="id"
                          multiple chips small-chips deletable-chips clearable
                          :search-input.sync="organizationSearch"
                          :loading="loadingOrganizations"
                          :disabled="!form.provisioningPolicy.webapp.enabled"
                          hide-no-data
                      />
                    </v-col>
                  </v-row>
                </v-card>

                <v-alert v-if="!hasTarget('portal') && !hasTarget('webapp')" type="info" text>
                  {{ $t('sso.allowed-targets') }}
                </v-alert>
              </v-tab-item>

              <!-- Claim Mappings tab -->
              <v-tab-item>
                <v-row class="mb-2">
                  <v-col class="text-right">
                    <v-btn small @click="addClaimMapping">
                      <v-icon left small>mdi-plus</v-icon>
                      {{ $t('sso.add-claim-mapping') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-card v-for="(mapping, index) in form.claimMappings" :key="index" outlined class="pa-4 mb-3">
                  <v-row align="center">
                    <v-col cols="12" md="1">
                      <v-switch v-model="mapping.enabled" :label="$t('sso.enabled')" hide-details dense />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select v-model="mapping.target" :label="$t('sso.target')" :items="mappingTargetOptions" dense hide-details />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="mapping.claim" :label="$t('sso.claim')" dense hide-details />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-select v-model="mapping.operator" :label="$t('sso.operator')" :items="operatorOptions" dense hide-details />
                    </v-col>
                    <v-col cols="12" md="2">
                      <v-text-field v-model="mapping.expectedValue" :label="$t('sso.expected-value')" dense hide-details />
                    </v-col>
                    <v-col v-if="mapping.target === 'portal'" cols="12" md="2">
                      <v-select v-model="mapping.portalRole" :label="$t('sso.portal-role')" :items="roleOptions" dense hide-details />
                    </v-col>
                    <template v-if="mapping.target === 'webapp'">
                      <v-col cols="12" md="2">
                        <v-select v-model="mapping.memberTypeId" :label="$t('sso.member-type')" :items="memberTypeItems" item-text="name" item-value="id" dense hide-details clearable />
                      </v-col>
                    </template>
                    <v-col cols="12" md="1">
                      <v-btn icon small @click="removeClaimMapping(index)"><v-icon small>mdi-delete</v-icon></v-btn>
                    </v-col>
                  </v-row>
                  <v-row v-if="mapping.target === 'webapp'" class="mt-2">
                    <v-col cols="12" md="6" offset-md="3">
                      <v-select v-model="mapping.memberGroupIds" :label="$t('sso.member-groups')" :items="memberGroupItems" item-text="name" item-value="id" multiple chips small-chips deletable-chips dense hide-details />
                    </v-col>
                  </v-row>
                </v-card>
              </v-tab-item>

              <!-- History tab -->
              <v-tab-item v-if="!isNew">
                <v-row>
                  <v-col>
                    <v-data-table
                        :headers="historyHeaders"
                        :items="historyItems"
                        :loading="loadingHistory"
                        :items-per-page="20"
                        dense
                    >
                      <template #item.action="{item}">
                        <v-chip x-small :color="historyActionColor(item.action)">
                          {{ $t('sso.attempt-action.' + item.action) }}
                        </v-chip>
                        <span v-if="item.failureReason" class="ml-1 error--text caption">{{ item.failureReason }}</span>
                      </template>
                      <template #item.createdAt="{value}">
                        {{ formatUtcDate(value, $t('format_datetime')) }}
                      </template>
                      <template #item.details="{item}">
                        <v-btn icon x-small @click="toggleRawClaims(item)">
                          <v-icon x-small>{{ expandedAttempt === item.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>

                    <v-expand-transition>
                      <v-card v-if="expandedAttempt && expandedAttemptData" outlined class="pa-4 mt-2 mb-4">
                        <h4 class="mb-2">{{ $t('sso.raw-claims') }}</h4>
                        <pre class="raw-claims-json">{{ JSON.stringify(expandedAttemptData.rawClaims, null, 2) }}</pre>
                      </v-card>
                    </v-expand-transition>

                    <v-alert v-if="!loadingHistory && historyItems.length === 0" type="info" text>
                      {{ $t('sso.history-empty') }}
                    </v-alert>
                  </v-col>
                </v-row>
              </v-tab-item>

            </v-tabs-items>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn small @click="cancelEdit">{{ $t('cancel') }}</v-btn>
            <v-btn small color="primary" class="ml-3" @click="save" :loading="saving">
              {{ $t('save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>
    </div>
  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import SSORepository from "@repository/SSORepository";
import MemberTypeRepository from "@repository/MemberTypeRepository";
import MemberGroupRepository from "@repository/MemberGroupRepository";
import OrganizationRepository from "@repository/OrganizationRepository";
import RulesMixin from "@mixins/RulesMixin";

export default {
  components: {AuditTrailButton},
  mixins: [RulesMixin],

  data() {
    return {
      loading: false,
      saving: false,
      editing: false,
      isNew: true,
      editingId: null,
      formTab: 0,
      providers: [],
      memberTypeItems: [],
      memberGroupItems: [],
      loadingMemberTypes: false,
      loadingMemberGroups: false,
      organizationItems: [],
      organizationSearch: null,
      loadingOrganizations: false,
      providerUrls: null,
      // History
      historyItems: [],
      loadingHistory: false,
      expandedAttempt: null,
      // Form
      form: this.defaultForm(),
    }
  },

  mounted() {
    this.loadProviders()
    if (this.$route.params.idpId) {
      this.editProvider({ id: this.$route.params.idpId })
    }
  },

  watch: {
    organizationSearch(val) {
      if (val && val.length >= 2) {
        this.searchOrganizations(val)
      }
    },

    'form.allowedTargets'(targets) {
      if (targets.includes('portal') && !this.form.provisioningPolicy.portal) {
        this.$set(this.form.provisioningPolicy, 'portal', this.defaultPortalProvisioning())
      }
      if (targets.includes('webapp') && !this.form.provisioningPolicy.webapp) {
        this.$set(this.form.provisioningPolicy, 'webapp', this.defaultWebappProvisioning())
        this.loadMemberTypesAndGroups()
      }
    },

    formTab(val) {
      // Load history when switching to history tab (last tab)
      if (!this.isNew && val === 4 && this.historyItems.length === 0) {
        this.loadHistory()
      }
    },
  },

  computed: {
    organization() {
      return this.$parent.organization ?? null
    },

    isOidc() {
      return this.form.protocol === 'oidc'
    },

    isSaml() {
      return this.form.protocol === 'saml'
    },

    isTokenIntrospection() {
      return this.form.protocol === 'token_introspection'
    },

    protocolLabel() {
      const opt = this.protocolOptions.find(o => o.value === this.form.protocol)
      return opt ? opt.text : this.form.protocol
    },

    headers() {
      return [
        { text: this.$t('sso.name'), value: 'name' },
        { text: this.$t('sso.slug'), value: 'slug' },
        { text: this.$t('sso.protocol'), value: 'protocol', width: 120 },
        { text: this.$t('sso.enabled'), value: 'enabled', width: 100 },
        { text: '', value: 'actions', sortable: false, width: 60 },
      ]
    },

    historyHeaders() {
      return [
        { text: 'Email', value: 'email' },
        { text: 'Action', value: 'action', width: 150 },
        { text: this.$t('sso.protocol'), value: 'protocol', width: 80 },
        { text: 'IP', value: 'ipAddress', width: 130 },
        { text: 'Date', value: 'createdAt', width: 180 },
        { text: '', value: 'details', sortable: false, width: 50 },
      ]
    },

    urlRows() {
      if (!this.providerUrls) return []
      const rows = []
      if (this.providerUrls.portalLoginUrl) rows.push({ label: 'Portal — URL de connexion', value: this.providerUrls.portalLoginUrl })
      if (this.providerUrls.portalCallbackUrl) rows.push({ label: 'Portal — Redirect URI', value: this.providerUrls.portalCallbackUrl })
      if (this.providerUrls.webappLoginUrl) rows.push({ label: 'WebApp — URL de connexion', value: this.providerUrls.webappLoginUrl })
      if (this.providerUrls.webappCallbackUrl) rows.push({ label: 'WebApp — Redirect URI', value: this.providerUrls.webappCallbackUrl })
      if (this.providerUrls.portalTokenLoginUrl) rows.push({ label: 'Portal — Token Login URL', value: this.providerUrls.portalTokenLoginUrl })
      if (this.providerUrls.webappTokenLoginUrl) rows.push({ label: 'WebApp — Token Login URL', value: this.providerUrls.webappTokenLoginUrl })
      if (this.providerUrls.issuerDiscoveryUrl) rows.push({ label: 'OIDC Discovery', value: this.providerUrls.issuerDiscoveryUrl })
      if (this.providerUrls.samlMetadataUrl) rows.push({ label: this.$t('sso.saml-metadata-url'), value: this.providerUrls.samlMetadataUrl })
      return rows
    },

    expandedAttemptData() {
      return this.historyItems.find(a => a.id === this.expandedAttempt)
    },

    protocolOptions() {
      return [
        { text: 'OIDC', value: 'oidc' },
        { text: 'SAML', value: 'saml' },
        { text: 'Token Introspection (OAuth 2.0)', value: 'token_introspection' },
      ]
    },

    targetOptions() {
      return [
        { text: 'Portal', value: 'portal' },
        { text: 'WebApp', value: 'webapp' },
      ]
    },

    mappingTargetOptions() {
      return this.form.allowedTargets.map(t => ({
        text: t === 'portal' ? 'Portal' : 'WebApp',
        value: t,
      }))
    },

    accountLinkPolicyOptions() {
      return [
        { text: this.$t('sso.account-link-policies.email_if_verified'), value: 'email_if_verified' },
        { text: this.$t('sso.account-link-policies.email_only'), value: 'email_only' },
        { text: this.$t('sso.account-link-policies.manual_only'), value: 'manual_only' },
      ]
    },

    syncModeOptions() {
      return [
        { text: this.$t('sso.sync-modes.provision_only'), value: 'provision_only' },
        { text: this.$t('sso.sync-modes.sync_on_login'), value: 'sync_on_login' },
      ]
    },

    operatorOptions() {
      return [
        { text: this.$t('sso.operators.equals'), value: 'equals' },
        { text: this.$t('sso.operators.contains'), value: 'contains' },
        { text: this.$t('sso.operators.in'), value: 'in' },
      ]
    },

    nameIdFormatOptions() {
      return [
        { text: 'Email', value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress' },
        { text: 'Unspecified', value: 'urn:oasis:names:tc:SAML:1.1:nameid-format:unspecified' },
        { text: 'Persistent', value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:persistent' },
        { text: 'Transient', value: 'urn:oasis:names:tc:SAML:2.0:nameid-format:transient' },
      ]
    },

    roleOptions() {
      return [
        { text: this.$t('enums.role.FLEET_MANAGER'), value: 'FLEET_MANAGER' },
        { text: this.$t('enums.role.ADMIN'), value: 'ADMIN' },
        { text: this.$t('enums.role.ADMIN_PLUS'), value: 'ADMIN_PLUS' },
        { text: this.$t('enums.role.FINANCE'), value: 'FINANCE' },
        { text: this.$t('enums.role.OPERATOR'), value: 'OPERATOR' },
        { text: this.$t('enums.role.CPO'), value: 'CPO' },
        { text: this.$t('enums.role.ACCOUNTING'), value: 'ACCOUNTING' },
        { text: this.$t('enums.role.TECHNICIAN'), value: 'TECHNICIAN' },
        { text: this.$t('enums.role.CONTROL'), value: 'CONTROL' },
      ]
    },
  },

  methods: {
    hasTarget(target) {
      return this.form.allowedTargets.includes(target)
    },

    defaultOidc() {
      return {
        issuer: '',
        clientId: '',
        clientSecret: '',
        scopes: ['openid', 'email', 'profile'],
        discoveryEnabled: true,
        authorizationEndpoint: null,
        tokenEndpoint: null,
        jwksUri: null,
        endSessionEndpoint: null,
        userinfoEndpoint: null,
      }
    },

    defaultSaml() {
      return {
        idpEntityId: '',
        idpSsoUrl: '',
        idpSloUrl: '',
        idpCertificate: '',
        idpMetadataUrl: '',
        spCertificate: '',
        spPrivateKey: '',
        nameIdFormat: 'urn:oasis:names:tc:SAML:1.1:nameid-format:emailAddress',
        signRequests: false,
        wantAssertionsSigned: true,
        wantResponseSigned: false,
      }
    },

    defaultTokenIntrospection() {
      return {
        introspectionEndpoint: '',
        clientId: '',
        clientSecret: '',
      }
    },

    defaultPortalProvisioning() {
      return { enabled: true, extraOrganizationIds: [], defaultRole: 'FLEET_MANAGER' }
    },

    defaultWebappProvisioning() {
      return { enabled: true, autoCreateMember: true, extraOrganizationIds: [], defaultMemberTypeId: null, defaultMemberGroupIds: [] }
    },

    defaultForm() {
      return {
        slug: '',
        name: '',
        protocol: 'oidc',
        allowedTargets: ['portal'],
        accountLinkPolicy: 'email_if_verified',
        requireVerifiedEmail: true,
        syncMode: 'provision_only',
        clockSkewSeconds: 30,
        enabled: true,
        oidc: this.defaultOidc(),
        saml: this.defaultSaml(),
        tokenIntrospection: this.defaultTokenIntrospection(),
        provisioningPolicy: { portal: this.defaultPortalProvisioning() },
        claimMappings: [],
      }
    },

    loadProviders() {
      if (!this.organization) return
      this.loading = true
      SSORepository.list(this.organization.id).then(data => {
        this.providers = data
        this.loading = false
      }).catch(() => {
        this.providers = []
        this.loading = false
      })
    },

    searchOrganizations(query) {
      this.loadingOrganizations = true
      OrganizationRepository.search({ searchText: query, offset: 0, limit: 20 }).then(response => {
        this.organizationItems = response.items || []
        this.loadingOrganizations = false
      }).catch(() => { this.loadingOrganizations = false })
    },

    loadMemberTypesAndGroups() {
      if (!this.organization || this.memberTypeItems.length > 0) return
      this.loadingMemberTypes = true
      MemberTypeRepository.search({ organizationId: this.organization.id, offset: 0, limit: 100 }).then(response => {
        this.memberTypeItems = response.items || []
        this.loadingMemberTypes = false
      }).catch(() => { this.loadingMemberTypes = false })
      this.loadingMemberGroups = true
      MemberGroupRepository.search({ organizationId: this.organization.id, offset: 0, limit: 100 }).then(response => {
        this.memberGroupItems = response.items || []
        this.loadingMemberGroups = false
      }).catch(() => { this.loadingMemberGroups = false })
    },

    loadHistory() {
      this.loadingHistory = true
      SSORepository.authenticationAttempts({ identityProviderId: this.editingId, limit: 50 }).then(data => {
        this.historyItems = data
        this.loadingHistory = false
      }).catch(() => {
        this.historyItems = []
        this.loadingHistory = false
      })
    },

    preloadExtraOrgs(ids) {
      if (!ids || ids.length === 0) return
      ids.forEach(id => {
        if (!this.organizationItems.find(o => o.id === id)) {
          OrganizationRepository.get(id).then(org => {
            this.organizationItems.push(org)
          }).catch(() => {})
        }
      })
    },

    createNew() {
      this.isNew = true
      this.editingId = null
      this.providerUrls = null
      this.historyItems = []
      this.expandedAttempt = null
      this.form = this.defaultForm()
      this.form.organizationId = this.organization.id
      this.formTab = 0
      this.editing = true
    },

    editProvider(provider) {
      this.showLoading(true)
      SSORepository.get(provider.id).then(data => {
        this.showLoading(false)
        this.isNew = false
        this.editingId = data.id
        this.providerUrls = data.urls || null
        this.historyItems = []
        this.expandedAttempt = null
        this.formTab = 0

        const provPolicy = data.provisioningPolicy || {}

        this.form = {
          slug: data.slug,
          name: data.name,
          protocol: data.protocol,
          allowedTargets: data.allowedTargets || ['portal'],
          accountLinkPolicy: data.accountLinkPolicy || 'email_if_verified',
          requireVerifiedEmail: data.requireVerifiedEmail ?? true,
          syncMode: data.syncMode || 'provision_only',
          clockSkewSeconds: data.clockSkewSeconds ?? 30,
          enabled: data.enabled ?? true,
          oidc: { ...this.defaultOidc(), ...(data.oidc || {}) },
          saml: { ...this.defaultSaml(), ...(data.saml || {}) },
          tokenIntrospection: { ...this.defaultTokenIntrospection(), ...(data.tokenIntrospection || {}) },
          provisioningPolicy: {
            ...(provPolicy.portal ? { portal: { ...this.defaultPortalProvisioning(), ...provPolicy.portal } } : {}),
            ...(provPolicy.webapp ? { webapp: { ...this.defaultWebappProvisioning(), ...provPolicy.webapp } } : {}),
          },
          claimMappings: (data.claimMappings || []).map(m => ({ ...m })),
        }

        // Ensure provisioning keys exist for all targets
        if (this.form.allowedTargets.includes('portal') && !this.form.provisioningPolicy.portal) {
          this.form.provisioningPolicy.portal = this.defaultPortalProvisioning()
        }
        if (this.form.allowedTargets.includes('webapp') && !this.form.provisioningPolicy.webapp) {
          this.form.provisioningPolicy.webapp = this.defaultWebappProvisioning()
        }

        if (this.form.allowedTargets.includes('webapp')) {
          this.loadMemberTypesAndGroups()
          this.preloadExtraOrgs(this.form.provisioningPolicy.webapp?.extraOrganizationIds)
        }
        if (this.form.allowedTargets.includes('portal')) {
          this.preloadExtraOrgs(this.form.provisioningPolicy.portal?.extraOrganizationIds)
        }

        this.editing = true
      }).catch(e => {
        this.showLoading(false)
        this.notifyError(e)
      })
    },

    goToProvider(provider) {
      this.$router.push({ name: 'organizations.sso.edit', params: { id: this.$route.params.id, idpId: provider.id } })
    },

    cancelEdit() {
      this.editing = false
      this.providerUrls = null
      if (this.$route.params.idpId) {
        this.$router.push({ name: 'organizations.sso', params: { id: this.$route.params.id } })
      }
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$dialog.notify.success('Copied')
      })
    },

    toggleRawClaims(item) {
      this.expandedAttempt = this.expandedAttempt === item.id ? null : item.id
    },

    historyActionColor(action) {
      const colors = { login: 'success', link: 'info', provision: 'primary', rejected: 'error' }
      return colors[action] || 'grey'
    },

    addClaimMapping() {
      const defaultTarget = this.form.allowedTargets[0] || 'portal'
      const mapping = { enabled: true, claim: '', operator: 'equals', expectedValue: '', target: defaultTarget }
      if (defaultTarget === 'portal') {
        mapping.portalRole = 'FLEET_MANAGER'
      } else {
        mapping.memberTypeId = null
        mapping.memberGroupIds = []
      }
      this.form.claimMappings.push(mapping)
    },

    removeClaimMapping(index) {
      this.form.claimMappings.splice(index, 1)
    },

    buildSaveData() {
      // Build the payload matching the API create/update format
      const data = {
        slug: this.form.slug,
        name: this.form.name,
        protocol: this.form.protocol,
        allowedTargets: this.form.allowedTargets,
        accountLinkPolicy: this.form.accountLinkPolicy,
        requireVerifiedEmail: this.form.requireVerifiedEmail,
        syncMode: this.form.syncMode,
        clockSkewSeconds: this.form.clockSkewSeconds,
        enabled: this.form.enabled,
        claimMappings: this.form.claimMappings,
      }

      if (this.isNew) {
        data.organizationId = this.form.organizationId
      }

      // Protocol-specific fields (flat for create/update per API spec)
      if (this.form.protocol === 'oidc') {
        data.issuer = this.form.oidc.issuer
        data.clientId = this.form.oidc.clientId
        if (this.form.oidc.clientSecret) {
          data.clientSecret = this.form.oidc.clientSecret
        }
        data.scopes = this.form.oidc.scopes
        data.discoveryEnabled = this.form.oidc.discoveryEnabled
        if (!this.form.oidc.discoveryEnabled) {
          data.authorizationEndpoint = this.form.oidc.authorizationEndpoint
          data.tokenEndpoint = this.form.oidc.tokenEndpoint
          data.jwksUri = this.form.oidc.jwksUri
          data.endSessionEndpoint = this.form.oidc.endSessionEndpoint
          data.userinfoEndpoint = this.form.oidc.userinfoEndpoint
        }
      } else if (this.form.protocol === 'saml') {
        data.samlIdpEntityId = this.form.saml.idpEntityId
        data.samlIdpSsoUrl = this.form.saml.idpSsoUrl
        data.samlIdpSloUrl = this.form.saml.idpSloUrl || null
        data.samlIdpCertificate = this.form.saml.idpCertificate || null
        data.samlIdpMetadataUrl = this.form.saml.idpMetadataUrl || null
        data.samlNameIdFormat = this.form.saml.nameIdFormat
        data.samlSignRequests = this.form.saml.signRequests
        data.samlWantAssertionsSigned = this.form.saml.wantAssertionsSigned
        data.samlWantResponseSigned = this.form.saml.wantResponseSigned
        if (this.form.saml.signRequests) {
          data.samlSpCertificate = this.form.saml.spCertificate || null
          data.samlSpPrivateKey = this.form.saml.spPrivateKey || null
        }
      } else if (this.form.protocol === 'token_introspection') {
        data.introspectionEndpoint = this.form.tokenIntrospection.introspectionEndpoint
        data.clientId = this.form.tokenIntrospection.clientId
        if (this.form.tokenIntrospection.clientSecret) {
          data.clientSecret = this.form.tokenIntrospection.clientSecret
        }
      }

      // Provisioning
      const cleanPolicy = {}
      if (data.allowedTargets.includes('portal') && this.form.provisioningPolicy.portal) {
        cleanPolicy.portal = this.form.provisioningPolicy.portal
      }
      if (data.allowedTargets.includes('webapp') && this.form.provisioningPolicy.webapp) {
        cleanPolicy.webapp = this.form.provisioningPolicy.webapp
      }
      data.provisioningPolicy = cleanPolicy

      return data
    },

    save() {
      if (!this.$refs.form.validate()) return

      this.saving = true
      const data = this.buildSaveData()

      const promise = this.isNew
          ? SSORepository.create(data)
          : SSORepository.update(this.editingId, data)

      promise.then((response) => {
        this.$dialog.notify.success(this.$t(this.isNew ? 'sso.created-success' : 'sso.updated-success'))
        this.saving = false
        if (this.isNew && response.id) {
          this.isNew = false
          this.editingId = response.id
          this.providerUrls = response.urls || null
          this.$router.replace({ name: 'organizations.sso.edit', params: { id: this.$route.params.id, idpId: response.id } })
        } else {
          this.providerUrls = response.urls || this.providerUrls
        }
        this.loadProviders()
      }).catch(e => {
        this.notifyError(e)
        this.saving = false
      })
    },

    deleteProvider(provider) {
      this.$dialog.confirm({
        text: this.$t('sso.delete-confirm'),
        actions: {
          false: this.$t('cancel'),
          true: { text: this.$t('delete'), color: 'error' },
        },
      }).then(result => {
        if (result) {
          this.showLoading(true)
          SSORepository.delete(provider.id).then(() => {
            this.$dialog.notify.success(this.$t('sso.deleted-success'))
            this.showLoading(false)
            this.loadProviders()
          }).catch(e => {
            this.notifyError(e)
            this.showLoading(false)
          })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.raw-claims-json {
  background: #f5f5f5;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  max-height: 400px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
